import React from "react";
import { MessageCircle, ExternalLink } from "lucide-react";
import { WhatsappGroup } from "../../types/student";

interface WhatsAppGroupsProps {
  groups: WhatsappGroup[];
  isLoading?: boolean;
  error?: string | null;
}

const WhatsAppGroups: React.FC<WhatsAppGroupsProps> = ({
  groups,
  isLoading = false,
  error = null,
}) => {
  if (isLoading) {
    return (
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg lg:text-xl font-bold text-slate-900">
            WhatsApp Groups
          </h2>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded w-3/4"></div>
          <div className="h-4 bg-slate-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg lg:text-xl font-bold text-slate-900">
            WhatsApp Groups
          </h2>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
        </div>
        <div className="text-center py-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg lg:text-xl font-bold text-slate-900">
            WhatsApp Groups
          </h2>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
        </div>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gradient-to-r from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <MessageCircle className="h-8 w-8 text-slate-500" />
          </div>
          <p className="text-slate-600 text-sm">
            No WhatsApp groups available yet
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6 hover:shadow-lg transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg lg:text-xl font-bold text-slate-900">
          WhatsApp Groups
        </h2>
        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
          <MessageCircle className="h-5 w-5 text-white" />
        </div>
      </div>

      <div className="space-y-3">
        {groups.map((group, index) => (
          <div
            key={`${group.type}-${group.code}-${index}`}
            className="flex items-center justify-between p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl hover:shadow-md transition-all duration-300 border border-green-200/50"
          >
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                <MessageCircle className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-slate-900 truncate">
                  {group.name}
                </h3>
                <p className="text-sm text-slate-600 truncate">
                  {group.code}
                  {group.level && (
                    <span className="ml-2 text-slate-500">
                      • Level {group.level}
                    </span>
                  )}
                  <span className="ml-2 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 capitalize">
                    {group.type}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <a
                href={group.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 text-xs font-semibold rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Join
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WhatsAppGroups;
